import { FC } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import SidebarMenuItem from 'src/components/ui/atoms/SidebarMenuItem'
import ProductFavoriteButton from 'src/components/ui/features/Sidebar/ProductFavoriteButton'
import useLoadDashboard from 'src/hooks/useLoadDashboard'
import { cn } from 'src/lib/cn'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import { AppMenuSavedDashboardInterface } from 'src/types/AppMenuInterface'
import { isTrueOrUndefinedFn } from 'src/utils/booleanUtils'
import { icFirstFn } from 'src/utils/textUtils'

interface SidebarProductDashboardProps {
  dashboard: AppMenuSavedDashboardInterface
  link: string
  subProductCode: string
  programId?: string
  className?: string
  isVisible?: boolean
  hasAccess?: boolean
  onClick?: () => void
  rightIconClassName?: string
  isOnModal?: boolean
  showActiveIndicator?: boolean
}

const SidebarProductDashboard: FC<SidebarProductDashboardProps> = ({
  dashboard,
  link,
  subProductCode,
  programId,
  className,
  isVisible,
  hasAccess,
  onClick,
  rightIconClassName,
  isOnModal,
  showActiveIndicator = true,
}) => {
  const { handleGetDashboard } = useLoadDashboard(subProductCode)
  const { selectedDashboard, setSelectedDashboard } = useSelectedDashboardStore()
  const selectedDashboardId = selectedDashboard?.id

  const {
    id,
    name,
    default_dashboard: isDefaultDashboard,
    display_on_left_nav: isDisplayOnLeftNav,
  } = dashboard

  const { pathname } = useLocation()
  const navigate = useNavigate()

  const isActive = id === selectedDashboardId

  const isNeedToShow = isTrueOrUndefinedFn(isDisplayOnLeftNav)

  if (!isNeedToShow) return null

  const handleClickDashboard = () => {
    if (!hasAccess) {
      return
    }

    const oldPathName = pathname

    const oldDashboard = selectedDashboard

    setSelectedDashboard(dashboard)
    handleGetDashboard({ id, programId }).catch(() => {
      setSelectedDashboard(oldDashboard)
      navigate(oldPathName, { replace: true })
    })
    onClick?.()
  }

  return (
    <SidebarMenuItem
      onClick={handleClickDashboard}
      label={icFirstFn(name)}
      active={isActive}
      to={hasAccess ? `${link}?dashboard_id=${id}` : undefined}
      className={cn('pl-12', 'hover:[&_.favorite-icon]:ml-[calc(100%-12px)]', className)}
      isVisible={isVisible}
      addon={!hasAccess ? `sidebar-addon-dashboard-${id}` : undefined}
      classes={{
        icon: 'text-loblolly mr-2!',
        rightIcon: cn(
          'ml-0 transition-all duration-300 ease-in-out flex-1',
          !dashboard.favorite && '[&_.product-favorite-button]:hidden!',
          !isActive && 'text-hit-gray',
          rightIconClassName,
        ),
        hoverRightIcon: cn(
          'ml-auto transition-all duration-300 ease-in-out',
          !dashboard.favorite && '[&_.product-favorite-button]:flex!',
        ),
      }}
      showActiveIndicator={showActiveIndicator}
      rightIcon={
        hasAccess && (
          <ProductFavoriteButton
            subProductCode={subProductCode}
            program={programId}
            dashboard={dashboard}
            className={cn('product-favorite-button flex flex-1 items-center')}
            iconClassName={cn(
              !isActive && 'hover:text-black',
              isOnModal && isActive && 'text-blue',
            )}
            enabled={!isDefaultDashboard}
          />
        )
      }
      isOnModal={isOnModal}
    />
  )
}

export default SidebarProductDashboard
